import { Helmet } from "react-helmet-async";
import { SITE, SITE_URL, absoluteUrl } from "@/lib/seo";

/**
 * Reusable SEO head manager (additive; renders nothing visible).
 * Handles: title, meta description, canonical, Open Graph, Twitter Card,
 * and optional JSON-LD structured data.
 *
 * Props:
 *  - title: page title (a " | ABAY360" suffix is appended unless titleFull)
 *  - titleFull: use title verbatim (no suffix)
 *  - description: meta description
 *  - path: route path used to build the canonical URL (e.g. "/properties")
 *  - image: absolute or relative OG/Twitter image URL
 *  - type: OpenGraph type (website | article | product)
 *  - noindex: adds robots noindex,nofollow when true
 *  - schema: a single JSON-LD object or an array of them
 */
export default function Seo({
  title,
  titleFull = false,
  description = SITE.description,
  path = "",
  image = SITE.ogImage,
  type = "website",
  noindex = false,
  schema,
}) {
  const fullTitle = title
    ? titleFull
      ? title
      : `${title} | ${SITE.name}`
    : `${SITE.name} — ${SITE.slogan}`;
  const canonical = absoluteUrl(path || (typeof window !== "undefined" ? window.location.pathname : "/"));
  const ogImage = image?.startsWith("http") ? image : absoluteUrl(image);
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={SITE.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.filter(Boolean).map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
